import { format, getISOWeek, getQuarter, parse, parseISO } from "date-fns";
import { getName, registerLocale } from "i18n-iso-countries";

import { DEFAULT_DECIMALS } from "@/helpers/constants";
import Lang from "@/lang";

// The locale is a JSON asset, not an ES module, so it is read via require.
// eslint-disable-next-line @typescript-eslint/no-var-requires
registerLocale(require("i18n-iso-countries/langs/en.json"));

// This variable defines the compact format that is used.
// We still have the standard compact format here and can switch to it by the customer request.
const IS_CUSTOM_COMPACT = true;

export const DATE_FORMAT = "yyyy-MM-dd";
export const VISUAL_DATE_FORMAT = "MM-dd-yyyy";
export const VISUAL_WEEK_FORMAT = "'W'ww yyyy";
export const VISUAL_MONTH_FORMAT = "MMM yy";
export const VISUAL_QUARTER_FORMAT = "'Q'q yy";

export const stringOrNumberToDate = (value: string | number): Date => {
  if (typeof value === "string") {
    try {
      const parts = value.match(/^(\d{1,4})-(\d{1,2})-(\d{1,2})/);

      if (parts) {
        return new Date(parseInt(parts[1], 10), parseInt(parts[2], 10) - 1, parseInt(parts[3], 10));
      }
    } catch (e) {
      console.error(e);
    }
  }

  return new Date(value);
};

const parseNumber = (
  val: number,
  config: Intl.NumberFormatOptions
): { integer: string; fraction: string; compact: string; unit: string; currency: string } => {
  const parts = new Intl.NumberFormat("en-NY", { ...config, notation: "compact" }).formatToParts(val as number);

  const prepared = parts.reduce((prev, curr) => {
    prev[curr.type] = curr.value;

    return prev;
  }, {} as Record<string, string>);

  return {
    integer: prepared?.integer || "0",
    fraction: prepared?.fraction || "0",
    compact: prepared?.compact || "",
    unit: prepared?.unit || "",
    currency: prepared?.currency || "",
  };
};

const compactFormatter = (val: number | null, config: Intl.NumberFormatOptions): string => {
  // Initially do not use config settings for fractions.
  const preparedConfig = {
    ...config,
    minimumFractionDigits: undefined,
    maximumFractionDigits: undefined,
  };

  // If the value is empty, just format `0` with the provided config.
  if (val === null || val === 0) {
    return new Intl.NumberFormat("en-NY", preparedConfig).format(0);
  }

  // Disassemble value using the 'parseNumber' function with a default maximum fraction digits setting.
  let parts = parseNumber(val, { ...preparedConfig, maximumFractionDigits: DEFAULT_DECIMALS });

  // If the integer part is 0, format the value using compact notation and return it
  // We use config?.maximumFractionDigits to overwrite preparedConfig declaration.
  if (Number(parts.integer) === 0) {
    return new Intl.NumberFormat("en-NY", {
      ...preparedConfig,
      notation: "compact",
      minimumFractionDigits: config?.maximumFractionDigits ?? DEFAULT_DECIMALS,
      maximumFractionDigits: config?.maximumFractionDigits ?? DEFAULT_DECIMALS,
    }).format(val as number);
  }

  // Check if the number is round (e.g., 1000) and disassemble it accordingly using 'parseNumber'.
  const isRound = Math.round(Number(`${parts.integer}.${parts.fraction}`)) === 1000;
  parts = parseNumber(val, {
    ...preparedConfig,
    maximumFractionDigits: isRound || config?.maximumFractionDigits === 0 ? 0 : 1,
  });

  // If the integer part is between 1 and 10, format the number using the parsed parts.
  if (Number(parts.integer) >= 1 && Number(parts.integer) < 10 && Number(parts.fraction) !== 0) {
    return `${parts.currency}${parts.integer}.${parts.fraction}${parts.compact}${parts.unit}`;
  }

  // If the integer part is 10 or above, or the fraction part is equal to zero,
  // format the number without fractions using the parsed parts.
  parts = parseNumber(val, { ...preparedConfig, maximumFractionDigits: 0 });
  return `${parts.currency}${parts.integer}${parts.compact ?? ""}${parts.unit}`;
};

export const configNumberFormatter: Record<string, Intl.NumberFormatOptions> = {
  currencyFormat: {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: DEFAULT_DECIMALS,
    maximumFractionDigits: DEFAULT_DECIMALS,
    notation: undefined,
  },
  integerCurrencyFormat: {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    notation: undefined,
  },
  percentFormat: {
    style: "percent",
    minimumFractionDigits: DEFAULT_DECIMALS,
    maximumFractionDigits: DEFAULT_DECIMALS,
    notation: undefined,
  },
  integerPercentFormat: { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 0, notation: undefined },
  integerFormat: { minimumFractionDigits: 0, maximumFractionDigits: 0, notation: undefined },
  numberFixedFormat: {
    minimumFractionDigits: DEFAULT_DECIMALS,
    maximumFractionDigits: DEFAULT_DECIMALS,
    notation: undefined,
  },
};

/* eslint-disable @typescript-eslint/no-explicit-any */
// Wide variants of config types.
type IFormatters = Record<string, (val: string | number | null, config?: any) => string | number>;

export const formatISODateLocal = (value: string | number | null, dateFormat = VISUAL_DATE_FORMAT): string | number => {
  if (value === undefined || value === "") return "";
  if (value === null) return Lang.null;

  let date: Date;

  if (typeof value === "string" && /^Q[1-4] \d{2,4}$/.test(value)) {
    const match = value.match(/^Q([1-4]) (\d{2,4})$/);
    if (match) {
      const quarter = parseInt(match[1], 10) - 1;
      let year = parseInt(match[2], 10);
      if (year < 100) year += 2000;
      date = new Date(Date.UTC(year, quarter * 3, 1, 12));
    } else {
      console.warn("formatDate received invalid quarter format:", value);
      return "";
    }
  } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    date = parse(value, "yyyy-MM-dd", new Date());
  } else {
    date = new Date(value as string | number);
  }

  if (isNaN(date.getTime())) return "";
  return format(date, dateFormat);
};

/** @deprecated Use formatISODateLocal for 'yyyy-MM-dd' strings to avoid TZ shift. */
export const formatDate = (value: string | number | null, dateFormat = VISUAL_DATE_FORMAT): string | number => {
  if (value === undefined || value === "") {
    return "";
  }

  if (value === null) {
    return Lang.null;
  }

  let date: Date;

  if (typeof value === "string" && /^Q[1-4] \d{2,4}$/.test(value)) {
    const match = value.match(/^Q([1-4]) (\d{2,4})$/);
    if (match) {
      const quarter = parseInt(match[1], 10) - 1; // Q1 → 0
      let year = parseInt(match[2], 10);
      if (year < 100) {
        year += 2000;
      }
      date = new Date(Date.UTC(year, quarter * 3, 1, 12));
    } else {
      console.warn("formatDate received invalid quarter format:", value);
      return "";
    }
  } else {
    date = new Date(value as string | number);
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  return format(date, dateFormat);
};

export const formatters: IFormatters = {
  codeToCountry: (val) => {
    if (val === null) {
      return Lang.unknown;
    }

    return getName(val, Lang.locale, { select: "alias" }) || Lang.unknown;
  },

  currencyFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.currencyFormat, ...config })
      : Intl.NumberFormat("en-NY", { ...configNumberFormatter.currencyFormat, ...config }).format(val as number);
  },

  integerCurrencyFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.integerCurrencyFormat, ...config })
      : new Intl.NumberFormat("en-NY", { ...configNumberFormatter.integerCurrencyFormat, ...config }).format(
          val as number
        );
  },

  integerPercentDirectFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    if (val === null) return "";

    const rounded = Math.round(val);

    const baseConfig: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: undefined,
      ...config,
    };

    if (config?.notation === "compact" && IS_CUSTOM_COMPACT) {
      const compact = compactFormatter(rounded, { ...baseConfig });
      return `${compact}%`;
    }

    const formatted = new Intl.NumberFormat("en-NY", baseConfig).format(rounded);
    return `${formatted}%`;
  },

  percentFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.percentFormat, ...config })
      : new Intl.NumberFormat("en-NY", { ...configNumberFormatter.percentFormat, ...config }).format(val as number);
  },

  compactPercentFormat: (val: string | number | null, config?: Intl.NumberFormatOptions): string | number => {
    if (typeof val !== "number" && val !== null) return val;

    const normalized = val === null ? null : val / 100;

    const minFrac = config?.minimumFractionDigits ?? 0;
    const maxFrac = config?.maximumFractionDigits ?? 2;

    return new Intl.NumberFormat("en-NY", {
      style: "percent",
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
      notation: undefined,
      ...config,
    }).format((normalized ?? 0) as number);
  },

  integerPercentFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.integerPercentFormat, ...config })
      : new Intl.NumberFormat("en-NY", { ...configNumberFormatter.integerPercentFormat, ...config }).format(
          val as number
        );
  },

  integerFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.integerFormat, ...config })
      : new Intl.NumberFormat(undefined, { ...configNumberFormatter.integerFormat, ...config }).format(val as number);
  },

  numberFixedFormat: (val, config: Intl.NumberFormatOptions): string => {
    if (typeof val !== "number" && val !== null) {
      return val;
    }

    return config?.notation === "compact" && IS_CUSTOM_COMPACT
      ? compactFormatter(val, { ...configNumberFormatter.numberFixedFormat, ...config })
      : new Intl.NumberFormat(undefined, { ...configNumberFormatter.numberFixedFormat, ...config }).format(
          val as number
        );
  },

  formatDate: (val) => formatISODateLocal(val, VISUAL_DATE_FORMAT),
  formatMonth: (val) => formatDate(val, VISUAL_MONTH_FORMAT),
  formatQuarter: (val) => formatDate(val, VISUAL_QUARTER_FORMAT),
  formatWeekLabel: (val) => {
    if (val === null || val === undefined || val === "") return "";
    const date = typeof val === "string" ? parseISO(val) : new Date(val as string | number);
    if (isNaN(date.getTime())) return "";
    const isoWeek = getISOWeek(date);
    const isoYear = date.getUTCFullYear();
    return `W${String(isoWeek).padStart(2, "0")} ${isoYear}`;
  },
  formatQuarterLabel: (val) => {
    if (val === null || val === undefined || val === "") return "";
    const date = typeof val === "string" ? parseISO(val) : new Date(val as string | number);
    if (isNaN(date.getTime())) return "";
    const q = getQuarter(date);
    const y = date.getUTCFullYear();
    return `Q${q} ${y}`;
  },

  stringFormat(val) {
    if (val === null) {
      return Lang.null;
    }

    return val;
  },

  firstElementOrStringTmpFormatter: (val): string => {
    if (Array.isArray(val)) {
      return val.length ? String(val[0]) : Lang.null;
    }

    if (typeof val === "string") {
      const parts = val.split(",");
      return parts.length ? parts[0].trim().concat("...") : Lang.null;
    }

    return Lang.null;
  },

  firstOrAllStates: (val): string => {
    if (val === null) return Lang.TBD;
    const states = val.toString().split(",");
    const numOfStates = states.length;
    if (Array.isArray(states) && numOfStates === 51) {
      return Lang.all;
    } else {
      return states[0].concat(numOfStates > 1 ? ` + ${numOfStates - 1}` : Lang.TBD);
    }
  },

  formattedCurrency: (val: string | number | null, config?: Intl.NumberFormatOptions): string => {
    const num = Number(val);
    if (val === null || val === undefined || val === "") return "";
    if (isNaN(num)) return "";

    return `$${num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...config,
    })}`;
  },

  formattedCurrencyNoFraction: (val: string | number | null): string => {
    const num = Number(val);
    if (val === null || val === undefined || val === "") return "";
    if (isNaN(num)) return "";

    return `$${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  },

  formattedCurrencyTwoDecimals: (val: string | number | null): string => {
    const num = Number(val);
    if (val === null || val === undefined || val === "") return "";
    if (isNaN(num)) return "";

    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },
  numberTwoDecimals: (val: string | number | null) => {
    const num = Number(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  },

  plainNumberWithSeparators: (val: string | number | null): string => {
    const num = Number(val);
    if (val === null || val === undefined || val === "") return "";
    if (isNaN(num)) return "";

    return num.toLocaleString("en-US", {
      notation: "standard",
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },

  monthOnly: (val) => {
    if (val === null || val === undefined || val === "") return "";
    let date: Date;

    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      date = parse(val, "yyyy-MM-dd", new Date());
    } else {
      date = new Date(val as string | number);
    }

    return format(date, "MMMM");
  },
};

export const formatter = (
  val: string | number | null,
  format: string | undefined,
  config?: { [key: string]: string | number }
): string | number | null => {
  if (!format) {
    return val;
  }

  return formatters[format] ? formatters[format](val, config) : val;
};
